import React, { useState, forwardRef, useCallback, useMemo } from "react";
import { Form } from "antd";
import _, { isEmpty, omit } from "lodash";
import Vocabulary from "components/Vocabulary";
import { FieldTypes } from "utils/formUtils";
import message from "components/CustomMessage";
import BaseModalForm from "components/BaseModalForm";
import { Environments } from "views/Findings/Constants";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { MODAL_SIZE_EXTRA_LARGE } from "constants/Layout";
import { uniqBy } from "utils/lodashUtils";
import { useHasDisabledEnvironment } from "contexts/GlobalSettingsContext";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import useUpdateEffect from 'hooks/useUpdateEffect';

const LeanIXMappingForm = forwardRef(
  ({ afterClose, loginConfigId, toolName, onUpdateConfigComponent, ...props }, ref) => {
    const [form] = Form.useForm();
    const [data, setData] = useState(() => getInitialData(props.config));
    const [factSheetType, setFactSheetType] = useState(() => props.config?.applicationType || null);
    const [factSheet, setFactSheet] = useState(() => props.config?.name || null);
    const [factSheetSubtype, setFactSheetSubtype] = useState(() => props.config?.otherProperties?.factSheetProperties?.factSheetSubType || null);
    const [factSheetAttributes, setFactSheetAttributes] = useState(() => {
      const attrs = props.config?.otherProperties?.factSheetProperties?.attributes;
      return attrs ? attrs.map(formatAttributeOption) : [];
    });
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    const featureFlags = useFeatureFlag();  

    useUpdateEffect(() => {
      if(factSheetType) {
          setFactSheet(null);
          form.setFieldsValue({factSheet: null, factSheetSubType: null, attributes: null});
      }
    },[factSheetType, form]);

    useUpdateEffect(() => {
      if(factSheet) {
          form.setFieldsValue({factSheetSubType: null, attributes: null});
      }
    },[factSheet, form]);

    const submitData = async (formData) => {
      let _envValue = formData.environment;
      if(hasDisabledEnvironment){
        _envValue = envValue;
      }
      const { project } = data;
      const sendRemoveId = !isEmpty(props.config) && project !== props.config.id;
      const filters = {...formData, environment: _envValue, id: project, toolName:"LEAN_IX"}
      if(sendRemoveId){
        filters.removedId = props.config.id;
      }
      await ToolsIntegrationsAPI.mapAssetTools(omit(filters, ["project"])).promise;
      message.success(`Mapping has been successfully saved`);
      onUpdateConfigComponent && onUpdateConfigComponent();
    };

    const baseModalProps = {
      okText: "Save",
      width: MODAL_SIZE_EXTRA_LARGE,
    };

    const factSheetApiParams = useMemo(() => ({
      applicationType: factSheetType
    }), [factSheetType]);

    const factSheetTypesApiParams = useMemo(() => ({
      applicationType: "FactSheetTypes"
    }), []);

    const getProjects = useCallback((params) => {
      const { promise, cancel } = ToolsIntegrationsAPI.getAsset({
        ...params,
        toolName: "LEAN_IX",
        showMapped: false,
        configId: loginConfigId
      });
      return { promise, cancel };
    }, [loginConfigId]);

    const getFactSheets = useCallback((params) => {
      const { promise, cancel } = ToolsIntegrationsAPI.getAsset({
        ...params,
        toolName: "LEAN_IX",
        showMapped: false,
        configId: loginConfigId, 
        applicationType: params.applicationType,
        name: params.name
      });
      return { promise, cancel };
    }, [loginConfigId]);
    
    const parseSelectOptionsForFactSheetTypes = useCallback(
      (options) => {
        const mappedOptions = options.map((c) => ({
          ...c,
          label: c.name,
          value: c.name,
        }));
    
        return uniqBy(mappedOptions, "value");
      },
      []
    );

    const parseFactSheetOptionsForFactSheet= useCallback((options) => {
      const mappedOptions = options.map((c) => ({
        ...c,
        label: c.name,
        value: c.name,
      }));
      
      return uniqBy(mappedOptions, "value");
    }, []);
    

    const handleFactSheetChange = useCallback((selectedFactSheet) => {
      getFactSheets({ name: selectedFactSheet}).promise
        .then((response) => {
          const attributes = response?.content[0].otherProperties.factSheetProperties.attributes;
          const formattedAttributes = attributes.map(formatAttributeOption);
          setFactSheetSubtype(response?.content[0].otherProperties.factSheetProperties?.factSheetSubType);
          setFactSheetAttributes(formattedAttributes);
          setData((data) => ({ ...data, project: response?.content[0].id}));

        })
        .catch((err) => {
          console.error("Error fetching fact sheets", err);
        });
    }, [getFactSheets]);


    const productSubProductEnvField = useMemo(() => [
      {
        name: "product",
        label: <>ArmorCode <Vocabulary name="product" isPlural={true} /></>,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        colSpan: 8,
        showSearch: true,
        required: true,
        props: {
          dropdownType: "product",
          showAddButton: true,
          onChange: (e) =>
            form.setFieldsValue({ subProduct: null, environment: null }),
          onCreate: (createdProduct) => {
            form.setFieldsValue({
              product: createdProduct.id,
              subProduct: null,
              environment: null,
            });
            setData({ ...data, product: createdProduct.id });
          },
          autoFocus: true,
        },
      },
      {
        name: "subProduct",
        label: <>ArmorCode <Vocabulary name="subProduct" isPlural={true} /></>,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        colSpan: 8,
        showSearch: true,
        required: true,
        props: {
          productId: data.product,
          showAddButton: !!data.product,
          dropdownType: "subProduct",
          onChange: () => form.setFieldsValue({ environment: null }),
          onCreate: (createdSubproduct) => {
            form.setFieldsValue({
              subProduct: createdSubproduct.id,
              environment: null,
            });
          },
        },
      },
      ...(hasDisabledEnvironment
        ? []
        : [
            {
              name: "environment",
              label: "ArmorCode Environment",
              type: FieldTypes.SELECT,
              colSpan: 8,
              showSearch: true,
              required: true,
              options: Environments,
            },
          ]),
    ], [data, hasDisabledEnvironment, form]);
    
    

    const fields = useMemo(
      () => [
        {
          name: "project",
          label: "System/IT Component",
          colSpan: 12,
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          required: true,
          props: {
            api: getProjects,
            parseSelectOptions: (options) => {
              const mappedOptions = options.map((c)=>{return {...c, label: c.name, value: c.id}});
              if(props.config){
                mappedOptions.push({label: props.config.name, value: props.config.id})
              }
              return uniqBy(mappedOptions, "value");
            },
            onChange: (value) => {
              setData({ ...data, project: value });
            },
          },
        },
        {
          colSpan: 12,
          render: () => <div />,
        },
      ...productSubProductEnvField
      ],
      [getProjects, productSubProductEnvField, props.config, data]
    );

    const fieldsV4 = useMemo(
      () => [
        {
          name: "factSheetType",
          label: "Fact Sheet Type",
          colSpan: 12,
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          required: true,
          props: {
            api: getFactSheets,
            apiParams: factSheetTypesApiParams,
            parseSelectOptions: parseSelectOptionsForFactSheetTypes,
            onChange: (value) => {
              setFactSheetType(value);
            },
          },
        },
        {
          name: "factSheet",
          label: "Fact Sheet",
          colSpan: 12,
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          required: true,
          props: {
            api: factSheetType ? getFactSheets : null,
            apiParams: factSheetApiParams,
            parseSelectOptions: parseFactSheetOptionsForFactSheet,
            resetOptionsProp: factSheetType,
            onChange: (value) => {
              setFactSheet(value);
              if(value){
                handleFactSheetChange(value);
              }
            },
          },
        },
        {
          name: "factSheetSubType",
          label: "Fact Sheet SubType",
          colSpan: 12,
          type: FieldTypes.SELECT,
          options: [{label:factSheetSubtype, value: factSheetSubtype}],
        },
        {
          name: "attributes",
          label: "Attributes",
          colSpan: 12,
          type: FieldTypes.MULTI_SELECT,
          required: true,
          options: factSheetAttributes,
        },
        ...productSubProductEnvField
      ],
      [getFactSheets, factSheetTypesApiParams, parseSelectOptionsForFactSheetTypes, factSheetType, factSheetApiParams, parseFactSheetOptionsForFactSheet, factSheetSubtype, factSheetAttributes, productSubProductEnvField, handleFactSheetChange]
    );

    const onValuesChange = useCallback((_, values) => {
      setData((data) => ({ ...data, ...values }));
    }, []);

    return (
      <BaseModalForm
        modalProps={baseModalProps}
        ref={ref}
        afterClose={afterClose}
        form={form}
        fields={featureFlags.ENABLE_LEANIX_V4_SUPPORT ? fieldsV4 : fields}
        onValuesChange={onValuesChange}
        initialValues={data}
        onOk={submitData}
        title={`Map LeanIX Systems`}
       />
    );
  }
);

export default LeanIXMappingForm;

function formatAttributeOption(attr) {
  let label;
  switch (attr) {
    case "description":
      label = "Description";
      break;
    case "quality_score":
      label = "Quality Score";
      break;
    case "tags":
      label = "Tags";
      break;
    default:
      label = attr;
  }
  return { label, value: attr };
}

function getInitialData(data = {}) {
  return {
    product: data.product,
    subProduct: data.subProduct,
    //intentionally set undefined else placeholder value does not show up
    environment: data.environment ? _.capitalize(data.environment) : undefined,
    project: data.id,
    factSheetType: data?.applicationType,
    factSheet: data?.name,
    factSheetSubType: data?.otherProperties?.factSheetProperties?.factSheetSubType,
    attributes: data?.otherProperties?.factSheetProperties?.attributes,
  };
}
